import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  supportService, 
  SupportTicket, 
  SupportStats, 
  SlaMetrics, 
  SupportAgentStatus 
} from '../../services/supportService';
import {
  TicketIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  UserGroupIcon,
  BoltIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

const SupportDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<SupportStats | null>(null);
  const [slaMetrics, setSlaMetrics] = useState<SlaMetrics | null>(null);
  const [availableAgents, setAvailableAgents] = useState<SupportAgentStatus[]>([]);
  const [showSlaPanel, setShowSlaPanel] = useState(false);
  const [showAgentPanel, setShowAgentPanel] = useState(false);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('OPEN');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [statusFilter, priorityFilter, categoryFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const filters: any = { limit: 50 };
      if (statusFilter) filters.status = statusFilter;
      if (priorityFilter) filters.priority = priorityFilter;
      if (categoryFilter) filters.category = categoryFilter;

      const [statsData, ticketsData, slaData, agentsData] = await Promise.all([
        supportService.getStats(),
        supportService.getTickets(filters),
        supportService.getSlaMetrics(30),
        supportService.getAvailableAgents(),
      ]);
      setStats(statsData);
      setTickets(ticketsData.tickets);
      setSlaMetrics(slaData);
      setAvailableAgents(agentsData);
    } catch (error) {
      console.error('Failed to load support data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (priority: string) => {
    const priorityColors = {
      LOW: 'bg-gray-100 text-gray-800',
      MEDIUM: 'bg-blue-100 text-blue-800',
      HIGH: 'bg-yellow-100 text-yellow-800',
      URGENT: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[priority as keyof typeof priorityColors]}`}>
        {priority}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      OPEN: 'bg-blue-100 text-blue-800',
      IN_PROGRESS: 'bg-yellow-100 text-yellow-800',
      RESOLVED: 'bg-green-100 text-green-800',
      CLOSED: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status as keyof typeof statusColors]}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getCategoryBadge = (category: string) => {
    const categoryColors = {
      PAYMENT: 'bg-green-100 text-green-800',
      TECHNICAL: 'bg-purple-100 text-purple-800',
      ACCOUNT: 'bg-blue-100 text-blue-800',
      KYC: 'bg-indigo-100 text-indigo-800',
      PAYOUT: 'bg-yellow-100 text-yellow-800',
      REFUND: 'bg-red-100 text-red-800',
      ABUSE: 'bg-red-100 text-red-800',
      OTHER: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${categoryColors[category as keyof typeof categoryColors]}`}>
        {category}
      </span>
    );
  };

  const handleAutoAssign = async (ticketId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await supportService.autoAssignTicket(ticketId);
      await loadData(); // Reload to show updated assignment
    } catch (error) {
      console.error('Failed to auto-assign ticket:', error);
      alert('Failed to auto-assign ticket. No available agents at this time.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Support Dashboard</h1>
        <p className="mt-2 text-sm text-gray-600">Manage support tickets and assist users</p>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0 }}
            className="relative overflow-hidden rounded-xl shadow-lg"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-600 opacity-90"></div>
            <div className="relative backdrop-blur-sm bg-white/20 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-white/30 rounded-lg backdrop-blur-sm">
                  <TicketIcon className="w-5 h-5 text-white" />
                </div>
                {stats.openTickets > 0 && (
                  <span className="bg-blue-700 text-white px-2 py-0.5 rounded-full text-xs font-bold shadow-lg">
                    {stats.openTickets}
                  </span>
                )}
              </div>
              <h3 className="text-2xl font-bold text-white">{stats.openTickets}</h3>
              <p className="text-white/90 text-xs mt-1 font-medium">Open Tickets</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative overflow-hidden rounded-xl shadow-lg"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-500 to-orange-600 opacity-90"></div>
            <div className="relative backdrop-blur-sm bg-white/20 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-white/30 rounded-lg backdrop-blur-sm">
                  <ClockIcon className="w-5 h-5 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white">{stats.inProgressTickets}</h3>
              <p className="text-white/90 text-xs mt-1 font-medium">In Progress</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative overflow-hidden rounded-xl shadow-lg"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-600 opacity-90"></div>
            <div className="relative backdrop-blur-sm bg-white/20 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-white/30 rounded-lg backdrop-blur-sm">
                  <CheckCircleIcon className="w-5 h-5 text-white" />
                </div>
                <ArrowTrendingUpIcon className="w-4 h-4 text-white/80" />
              </div>
              <h3 className="text-2xl font-bold text-white">{stats.resolvedToday}</h3>
              <p className="text-white/90 text-xs mt-1 font-medium">Resolved Today</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="relative overflow-hidden rounded-xl shadow-lg"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-pink-600 opacity-90"></div>
            <div className="relative backdrop-blur-sm bg-white/20 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-white/30 rounded-lg backdrop-blur-sm">
                  <ExclamationTriangleIcon className="w-5 h-5 text-white" />
                </div>
                {stats.urgentTickets > 0 && (
                  <span className="bg-red-700 text-white px-2 py-0.5 rounded-full text-xs font-bold shadow-lg animate-pulse">
                    URGENT
                  </span>
                )}
              </div>
              <h3 className="text-2xl font-bold text-white">{stats.urgentTickets}</h3>
              <p className="text-white/90 text-xs mt-1 font-medium">Urgent</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="relative overflow-hidden rounded-xl shadow-lg"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-indigo-600 opacity-90"></div>
            <div className="relative backdrop-blur-sm bg-white/20 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-white/30 rounded-lg backdrop-blur-sm">
                  <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white">{stats.totalTickets}</h3>
              <p className="text-white/90 text-xs mt-1 font-medium">Total Tickets</p>
            </div>
          </motion.div>
        </div>
      )}

      {/* Action Buttons Row */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mb-8">
        <button
          onClick={() => setShowSlaPanel(!showSlaPanel)}
          className="inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
        >
          <ChartBarIcon className="h-5 w-5 mr-2" />
          {showSlaPanel ? 'Hide' : 'Show'} SLA Metrics
        </button>
        <button
          onClick={() => setShowAgentPanel(!showAgentPanel)}
          className="inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
        >
          <UserGroupIcon className="h-5 w-5 mr-2" />
          {showAgentPanel ? 'Hide' : 'Show'} Agent Management
        </button>
      </div>

      {/* SLA Metrics Panel */}
      {showSlaPanel && slaMetrics && (
        <div className="bg-white shadow rounded-lg mb-8 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              SLA Metrics (Last {slaMetrics.windowDays} Days)
            </h2>
          </div>

          {/* Overall Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Total Tickets</div>
              <div className="text-3xl font-bold text-blue-600">{slaMetrics.totals.tickets}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">SLA Breaches</div>
              <div className="text-3xl font-bold text-red-600">{slaMetrics.totals.breaches}</div>
              <div className="text-sm text-gray-500 mt-1">
                {slaMetrics.totals.tickets > 0 
                  ? `${((slaMetrics.totals.breaches / slaMetrics.totals.tickets) * 100).toFixed(1)}% breach rate`
                  : '0% breach rate'}
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Avg First Response</div>
              <div className="text-3xl font-bold text-green-600">
                {slaMetrics.totals.avgFirstResponseHours.toFixed(1)}h
              </div>
            </div>
          </div>

          {/* By Priority */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {Object.entries(slaMetrics.byPriority).map(([priority, data]) => {
              const target = slaMetrics.targetsHours[priority as keyof typeof slaMetrics.targetsHours];
              const breachRate = data.breachRate * 100;
              const statusColor = breachRate > 20 ? 'red' : breachRate > 10 ? 'yellow' : 'green';
              
              return (
                <div key={priority} className={`bg-${statusColor}-50 rounded-lg p-4 border border-${statusColor}-200`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs font-semibold text-${statusColor}-800 uppercase`}>
                      {priority}
                    </span>
                    <span className={`text-xs text-${statusColor}-600`}>
                      Target: {target}h
                    </span>
                  </div>
                  <div className={`text-2xl font-bold text-${statusColor}-700 mb-1`}>
                    {data.open}
                  </div>
                  <div className="text-xs text-gray-600">
                    {data.breaches} breaches ({breachRate.toFixed(1)}%)
                  </div>
                  <div className="mt-2 bg-gray-200 rounded-full h-2">
                    <div 
                      className={`bg-${statusColor}-500 h-2 rounded-full transition-all`}
                      style={{ width: `${Math.min(breachRate, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Agent Management Panel */}
      {showAgentPanel && (
        <div className="bg-white shadow rounded-lg mb-8 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Available Agents</h2>
            <span className="text-sm text-gray-600">
              {availableAgents.filter(a => a.status === 'ONLINE').length} online
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availableAgents.length === 0 ? (
              <div className="col-span-full text-center py-8 text-gray-500">
                No agents available
              </div>
            ) : (
              availableAgents.map((agent) => {
                const statusColors = {
                  ONLINE: 'bg-green-100 text-green-800 border-green-300',
                  BUSY: 'bg-yellow-100 text-yellow-800 border-yellow-300',
                  AWAY: 'bg-gray-100 text-gray-800 border-gray-300',
                  OFFLINE: 'bg-red-100 text-red-800 border-red-300',
                };
                
                return (
                  <div 
                    key={agent.id} 
                    className={`border-2 rounded-lg p-4 ${statusColors[agent.status]}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {agent.user?.username?.slice(0, 2).toUpperCase() || '??'}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{agent.user?.username || 'Unknown'}</div>
                          <div className="text-xs text-gray-600">{agent.status}</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Current Load:</span>
                        <span className="font-medium">{agent.currentLoad} / {agent.maxConcurrent}</span>
                      </div>
                      {agent.stats && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Assigned:</span>
                            <span className="font-medium">{agent.stats.assignedTickets}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Resolved (30d):</span>
                            <span className="font-medium">{agent.stats.resolvedLast30Days}</span>
                          </div>
                          {agent.stats.avgResolutionTime && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Avg Resolution:</span>
                              <span className="font-medium">{agent.stats.avgResolutionTime.toFixed(1)}h</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-6 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            >
              <option value="">All Statuses</option>
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="RESOLVED">Resolved</option>
              <option value="CLOSED">Closed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            >
              <option value="">All Priorities</option>
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
              <option value="URGENT">Urgent</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            >
              <option value="">All Categories</option>
              <option value="PAYMENT">Payment</option>
              <option value="TECHNICAL">Technical</option>
              <option value="ACCOUNT">Account</option>
              <option value="KYC">KYC</option>
              <option value="PAYOUT">Payout</option>
              <option value="REFUND">Refund</option>
              <option value="ABUSE">Abuse</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Tickets</h2>
        </div>

        <div className="divide-y divide-gray-200">
          {tickets.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <TicketIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>No tickets found with current filters</p>
            </div>
          ) : (
            tickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => navigate(`/support/tickets/${ticket.id}`)}
                className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        #{ticket.id.slice(-8)} - {ticket.subject}
                      </h3>
                    </div>
                    <div className="flex items-center space-x-2 mb-3">
                      {getPriorityBadge(ticket.priority)}
                      {getStatusBadge(ticket.status)}
                      {getCategoryBadge(ticket.category)}
                    </div>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>User: {ticket.user?.username || 'Unknown'}</span>
                      <span>•</span>
                      <span>Created: {new Date(ticket.createdAt).toLocaleString()}</span>
                      {ticket.assignedTo && (
                        <>
                          <span>•</span>
                          <span>Assigned to: {ticket.assignedTo.username}</span>
                        </>
                      )}
                      {ticket._count?.messages && (
                        <>
                          <span>•</span>
                          <span>{ticket._count.messages} messages</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="ml-4 flex space-x-2">
                    {!ticket.assignedToId && (
                      <button 
                        onClick={(e) => handleAutoAssign(ticket.id, e)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                        title="Auto-assign to available agent"
                      >
                        <BoltIcon className="h-4 w-4 mr-1" />
                        Auto-assign
                      </button>
                    )}
                    <button className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500">
                      View
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportDashboard;
